import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  FormControl,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { trackEvent } from "../../../../features/analytics/analytics";
import { saveEventLog } from "../../../../features/analytics/analyticsBatcher";
import {
  CsvImportOptions,
  CsvResult,
  EssentialColumn,
  NO_COLUMN,
} from "../../../../features/import/csv";

/**
 * S112: the price, photo and description columns, picked like the code and name columns. A file
 * names them its own way ("Precio", "Regular price", "Imagenes"); the server finds them and fills
 * the fields every category has, and this is where the user sees that and can change it.
 */

const LABELS: Record<EssentialColumn["field"], string> = {
  Price: "Price",
  Image: "Photo",
  Description: "Description",
};

const OPTION_KEYS: Record<
  EssentialColumn["field"],
  "priceField" | "imageField" | "descriptionField"
> = {
  Price: "priceField",
  Image: "imageField",
  Description: "descriptionField",
};

/** Price and photo with no column, and most new products without one. Never a block. */
const missingEssentials = (result: CsvResult) => {
  const n = result.analysis.newProducts ?? 0;
  if (n === 0) return [];
  return (result.analysis.essentials ?? []).filter(
    (e) => e.field !== "Description" && !e.column && (e.filled ?? 0) * 2 < n,
  );
};

/**
 * B423: a photo column was read and no new product has a value in it. In an Excel file that is
 * pictures placed in the cells, which the import cannot see (it reads cell values only).
 */
const emptyImageColumn = (
  result: CsvResult,
  options: CsvImportOptions,
): string | null => {
  if ((result.analysis.newProducts ?? 0) === 0) return null;
  const image = (result.analysis.essentials ?? []).find(
    (e) => e.field === "Image",
  );
  if (!image?.column || (image.filled ?? 0) > 0) return null;
  // Counted for the column the server read; another pick is not counted yet.
  if ((options.imageField ?? image.column) !== image.column) return null;
  return image.column;
};

export const ImportCsvEssentials = ({
  result,
  options,
  setOptions,
}: {
  result: CsvResult;
  options: CsvImportOptions;
  setOptions: (o: CsvImportOptions) => void;
}) => {
  const essentials = result.analysis.essentials ?? [];
  if (essentials.length === 0) return null;

  const headers = result.analysis.headers.filter((h) => !!h);
  const newProducts = result.analysis.newProducts ?? 0;
  const missing = missingEssentials(result);
  const emptyImage = emptyImageColumn(result, options);

  return (
    <Box sx={{ my: 1 }}>
      {missing.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {newProducts === 1
            ? "The new product has"
            : `${newProducts} new products have`}{" "}
          no {missing.map((e) => LABELS[e.field].toLowerCase()).join(" or ")}:
          no column of this file was read as{" "}
          {missing.map((e) => LABELS[e.field]).join(" or ")}. If your file has
          one, pick it below. Otherwise they import without, and cards show the
          name and code.
        </Alert>
      )}
      {emptyImage && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          The &ldquo;{emptyImage}&rdquo; column is empty, so no product gets a
          photo. Pictures placed inside an Excel sheet are not read, only the
          text in the cells. Put each photo&rsquo;s link (URL) or file name in
          that column and upload the photos under Images, or import without
          photos and add them afterwards.
        </Alert>
      )}
      <Typography
        variant="body2"
        component="div"
        sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2 }}
      >
        {essentials.map((e) => {
          const key = OPTION_KEYS[e.field];
          const value = options[key] ?? e.column ?? NO_COLUMN;
          return (
            <Box
              key={e.field}
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <strong>{LABELS[e.field]}</strong> &larr;
              <FormControl size="small">
                <Select
                  sx={{ minWidth: 140 }}
                  value={headers.includes(value) ? value : NO_COLUMN}
                  onChange={(event) =>
                    setOptions({ ...options, [key]: event.target.value })
                  }
                  size="small"
                >
                  <MenuItem value={NO_COLUMN}>-- none --</MenuItem>
                  {headers.map((h, i) => (
                    <MenuItem key={i} value={h}>
                      {h}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {newProducts > 0 && !!e.column && value === e.column && (
                <Typography variant="caption" color="text.secondary">
                  {e.filled ?? 0} of {newProducts}
                  {e.how === "remembered" && " · as on your last import"}
                </Typography>
              )}
            </Box>
          );
        })}
      </Typography>
    </Box>
  );
};

const STORES: Record<string, { name: string; path: string }> = {
  woocommerce: { name: "WooCommerce", path: "/import/woocommerce" },
  shopify: { name: "Shopify", path: "/import/shopify" },
};

/**
 * B416: a store's own export, read as a sheet, loses its prices, photos and variants (12 of 13
 * WooCommerce-export importers in 90 days got nothing usable). The store import reads them; the
 * file stays allowed.
 */
export const StoreExportNotice = ({
  result,
  options,
  setOptions,
  onLeave,
}: {
  result: CsvResult;
  options: CsvImportOptions;
  setOptions: (o: CsvImportOptions) => void;
  onLeave: () => void;
}) => {
  const store = STORES[result?.analysis?.storeExport ?? ""];
  if (!store || !result.analysis.storeExportPending || options.keepStoreFile)
    return null;

  const choose = (choice: "store" | "file") =>
    trackEvent({
      category: "csv-import",
      action: "store-export",
      label: `${result.analysis.storeExport}:${choice}`,
      // A feature event reaches Analytics.Events (B416 reads it); a plain one is a trace only.
      feature: true,
    });

  return (
    <Alert severity="info" sx={{ my: 2 }}>
      <Box>
        <strong>This is a {store.name} export.</strong>{" "}
        Import from your {store.name} store instead: prices, photos and variants come across. A file
        import keeps only the columns it can read.
      </Box>
      <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
        <Button
          variant="contained"
          size="small"
          component={RouterLink}
          to={store.path}
          onClick={() => {
            choose("store");
            // Sent now: the user is leaving the import, and this is the choice B416 reads.
            saveEventLog("store-export");
            onLeave();
          }}
        >
          Import from {store.name}
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            choose("file");
            setOptions({ ...options, keepStoreFile: true });
          }}
        >
          Continue with this file
        </Button>
      </Box>
    </Alert>
  );
};
